import * as React from 'react';
import { Button } from '@/components/ui/button';
import type { DataValue, UserDefinedType, ArrayDataType } from '@glueapp/graph-types';
import { getDefaultValueForDataType } from '@glueapp/graph-types';
import { renderEditField } from './InputValueEditor';
import { Trash2, Plus } from 'lucide-react';

interface ArrayValueEditorProps {
    type: ArrayDataType;
    value: DataValue[];
    userDefinedType?: UserDefinedType;
    onChange: (value: DataValue[], updatedType?: UserDefinedType) => void;
}

export const ArrayValueEditor: React.FC<ArrayValueEditorProps> = ({ type, value, userDefinedType, onChange }) => {
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-foreground">
                    List {value.length > 0 && <span className="font-normal text-muted-foreground">({value.length})</span>}
                </label>
            </div>

            {value.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-border p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-2">No items</p>
                    <Button variant="outline" size="sm" onClick={() => onChange([...value, getDefaultValueForDataType(type.subType)])}>
                        <Plus className="h-3 w-3 mr-1" />
                        Add first item
                    </Button>
                </div>
            ) : (
                <div className="space-y-2">
                    {value.map((element, index) => (
                        <div key={index} className="rounded-md border-2 border-border bg-background shadow-sm">
                            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                                    Item {index + 1}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => onChange([...value.slice(0, index), ...value.slice(index + 1)])}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                            <div className="p-2.5">
                                {renderEditField(type.subType, element, userDefinedType, changedValue =>
                                    onChange([...value.slice(0, index), changedValue, ...value.slice(index + 1)]),
                                )}
                            </div>
                        </div>
                    ))}

                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => onChange([...value, getDefaultValueForDataType(type.subType)])}
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        Add item
                    </Button>
                </div>
            )}
        </div>
    );
};
